import { Injectable } from '@angular/core';

import { HomeCharacteristics, HomeOperatingConditions, WeatherConditions, InfiltrationParameters } from '../models/user-inputs';
import { STACK_COEFFICIENTS, WIND_COEFFICIENTS } from '../models/data';

@Injectable({
  providedIn: 'root'
})
export class HomeInfiltrationService {

  constructor() { }
  
  private houseVolume(homeCharacteristics: HomeCharacteristics): number {
    const floorArea = homeCharacteristics.floorArea; // m^2
    const houseCeilingHeight = 2.44; // m
    const houseVolume = floorArea * houseCeilingHeight; // m^3
    return houseVolume;
  }
  
  private buildingHeight(homeCharacteristics: HomeCharacteristics): number {
    const numberOfFloors = homeCharacteristics.numberOfFloors;
    const houseStoryHeight = 2.5; // meters
    const roofHeight = 0.5; // meters
    const buildingHeight = (numberOfFloors * houseStoryHeight) + roofHeight; // meters
    return buildingHeight;
  }
  
  private normalizedLeakage(homeCharacteristics: HomeCharacteristics): number {
    const beta0 = 20.7;
    const beta1 = -0.0107;
    const beta2 = -0.0022; // 1/m^2
    
    const floorArea = homeCharacteristics.floorArea; // m^2
    const yearBuilt = homeCharacteristics.yearBuilt;
    const normalizedLeakage = Math.exp(beta0 + (beta1 * yearBuilt) + (beta2 * floorArea));
    return normalizedLeakage;
  }
  
  private normalizationFactor(homeCharacteristics: HomeCharacteristics): number {
    const floorArea = homeCharacteristics.floorArea; // m^2
    const floorAreaCmSq = floorArea * Math.pow(100,2); // cm^2
    const buildingHeight = this.buildingHeight(homeCharacteristics);
    const normalizationFactor = (1000 / floorAreaCmSq) * Math.pow(buildingHeight / 2.5, 0.3); // 1/cm^2
    return normalizationFactor;
  }
  
  private airflowDueToInfiltration(homeCharacteristics: HomeCharacteristics,
    homeOperatingConditions: HomeOperatingConditions,
    weatherConditions: WeatherConditions): number {
    const normalizedLeakage = this.normalizedLeakage(homeCharacteristics);
    const normalizationFactor = this.normalizationFactor(homeCharacteristics);
    
    const leakageArea = normalizedLeakage / normalizationFactor; // cm^2
    
    const numberOfFloors = homeCharacteristics.numberOfFloors;
    const stackCoefficient = STACK_COEFFICIENTS[numberOfFloors];
    
    const windSheltering = homeCharacteristics.windShelteringOfHouse;
    const windCoefficient = WIND_COEFFICIENTS[windSheltering][numberOfFloors];
    
    const indoorTemperature = homeOperatingConditions.indoorTemperature; // ºC
    const outdoorTemperature = weatherConditions.temperature; // ºC
    
    const windSpeed = weatherConditions.windSpeed; // km/hr
    const windSpeedMS = windSpeed * 1000/3600;
    
    const airflowDueToInf = // L/s
      leakageArea * Math.sqrt((stackCoefficient * Math.abs(indoorTemperature - outdoorTemperature)) +
        (windCoefficient * Math.pow(windSpeedMS, 2)));
    return airflowDueToInf;
  }
  
  private areaOfWindowAndDoorOpenings(homeOperatingConditions: HomeOperatingConditions): number {
    const numberOfWindowsOpen = homeOperatingConditions.numberOfWindowsOpen;
    const openingHeightOfWindows = homeOperatingConditions.openingHeightOfWindows; // cm
    const durationWindowsOpen = homeOperatingConditions.durationWindowsOpen; // hours
    const widthOfWindow = 60.0; // cm
    const averageAreaOfOpenWindows = numberOfWindowsOpen*openingHeightOfWindows*widthOfWindow*durationWindowsOpen/24; // cm^2
    
    const numberOfDoorsOpen = homeOperatingConditions.numberOfDoorsOpen;
    const openingHeightOfDoors = homeOperatingConditions.openingHeightOfDoors; // cm
    const durationDoorsOpen = homeOperatingConditions.durationDoorsOpen; // hours
    const widthOfDoor = 76.2; // cm
    const averageAreaOfOpenDoors = numberOfDoorsOpen*openingHeightOfDoors*widthOfDoor*durationDoorsOpen/24; // cm^2
    
    const openArea = averageAreaOfOpenWindows + averageAreaOfOpenDoors; // cm^2
    return openArea;
  }
  
  private airflowDueToNaturalVentilationFromWind(homeOperatingConditions: HomeOperatingConditions,
    weatherConditions: WeatherConditions): number {
    const halfOpenArea = 0.5 * this.areaOfWindowAndDoorOpenings(homeOperatingConditions);
    const halfOpenAreaMSq = halfOpenArea / Math.pow(100,2); // m^2
    
    const windSpeed = weatherConditions.windSpeed; // km/hr
    const windSpeedMS = windSpeed * 1000/3600;
    
    const airflowDueToNatVentFromWind = 0.3 * halfOpenAreaMSq * windSpeedMS * 1000; // L/s
    return airflowDueToNatVentFromWind;
  }
  
  private airflowDueToNaturalVentilationFromStackEffect(homeCharacteristics: HomeCharacteristics,
    homeOperatingConditions: HomeOperatingConditions, weatherConditions: WeatherConditions): number {
    const indoorTemperature = homeOperatingConditions.indoorTemperature; // ºC
    const outdoorTemperature = weatherConditions.temperature; // ºC
    
    const halfOpenArea = 0.5 * this.areaOfWindowAndDoorOpenings(homeOperatingConditions);
    const halfOpenAreaMSq = halfOpenArea / Math.pow(100,2); // m^2
    
    const nplMidpointHeight = (0.5*this.buildingHeight(homeCharacteristics))-0.91 ;// m
    const tempDiff = indoorTemperature - outdoorTemperature;
    const maxTemp = Math.max(indoorTemperature, outdoorTemperature);
    const airflowDueToNatVentFromStackEffect = // L/s
      0.65*halfOpenAreaMSq*Math.sqrt(2*9.8*nplMidpointHeight*Math.abs(tempDiff)/maxTemp)*1000;
    return airflowDueToNatVentFromStackEffect;
  }
  
  private airflowDueToMechanicalVentilation(homeOperatingConditions: HomeOperatingConditions): number {
    const numberOfWindowFansOperating = homeOperatingConditions.numberOfWindowFansOperating;
    const durationWindowFansOperating = homeOperatingConditions.durationWindowFansOperating;
    const airflowOfWindowFans = homeOperatingConditions.airflowOfWindowFans; // cfm
    const airflowDueToMechVent = // L/s
      numberOfWindowFansOperating*airflowOfWindowFans*0.471947*durationWindowFansOperating/24;
    return airflowDueToMechVent;
  }

  homeAirExchangeRate(homeCharacteristics: HomeCharacteristics,
    homeOperatingConditions: HomeOperatingConditions, weatherConditions: WeatherConditions): number {
    const airflowDueToNatVentFromWind = this.airflowDueToNaturalVentilationFromWind(homeOperatingConditions, weatherConditions);
    
    const airflowDueToNatVentFromStackEffect = this.airflowDueToNaturalVentilationFromStackEffect(homeCharacteristics, homeOperatingConditions, weatherConditions);
    
    const airflowDueToNatVent = // L/s
      Math.sqrt(Math.pow(airflowDueToNatVentFromWind,2)+Math.pow(airflowDueToNatVentFromStackEffect,2));
    
    const airflowDueToInf = this.airflowDueToInfiltration(homeCharacteristics, homeOperatingConditions, weatherConditions);
    
    const airflowDueToInfNatVent = // L/s
      Math.sqrt(Math.pow(airflowDueToInf,2)+Math.pow(airflowDueToNatVent,2));
    
    const airflowDueToMechVent = this.airflowDueToMechanicalVentilation(homeOperatingConditions);
    
    const airflowDueToInfNatVentMechVent =
      Math.sqrt(Math.pow(airflowDueToInfNatVent,2) + Math.pow(airflowDueToMechVent,2));
    
    const homeAirExchangeRate = 3600 * airflowDueToInfNatVentMechVent / this.houseVolume(homeCharacteristics) / 1000;
    return homeAirExchangeRate;
  }
  
  private airCleanerRemovalRatePM25(homeCharacteristics: HomeCharacteristics,
    homeOperatingConditions: HomeOperatingConditions): number {
    const numberOfAirCleanersOperating = homeOperatingConditions.numberOfAirCleanersOperating;
    const durationAirCleanersOperating = homeOperatingConditions.durationAirCleanersOperating;
    const cleanAirDeliveryRate = homeOperatingConditions.cleanAirDeliveryRate;
    const airCleanerRemovalRate = // 1/hr
      (durationAirCleanersOperating/24)*60*numberOfAirCleanersOperating*cleanAirDeliveryRate*0.0283168/this.houseVolume(homeCharacteristics);
    return airCleanerRemovalRate;
  }
  
  homeInfiltrationFactor(pollutant: string, homeAirExchangeRate: number,
    homeCharacteristics: HomeCharacteristics, homeOperatingConditions: HomeOperatingConditions,
    infiltrationParameters: InfiltrationParameters): number {
    let homePenetrationCoefficient = 0;
    let homeRemovalRate = 0;
    let airCleanerRemovalRate = 0;
    if (pollutant == 'PM2.5') {
      homePenetrationCoefficient = infiltrationParameters.penetrationCoefficientPM25Home;
      homeRemovalRate = infiltrationParameters.removalRatePM25Home;
      airCleanerRemovalRate = this.airCleanerRemovalRatePM25(homeCharacteristics, homeOperatingConditions);
    } else if (pollutant == 'Ozone') {
      homePenetrationCoefficient = infiltrationParameters.penetrationCoefficientOzoneHome;
      homeRemovalRate = infiltrationParameters.removalRateOzoneHome;
    }
    const homeInfiltrationFactor = (homePenetrationCoefficient * homeAirExchangeRate) /
          (homeAirExchangeRate + homeRemovalRate + airCleanerRemovalRate)
    return homeInfiltrationFactor;
  }
}
